import { useEffect, useRef, useState } from 'react';
import type { PublicPhoto } from '@karozik/shared';
import { Avatar, cn, formatBytes, formatClock, Spinner } from '@karozik/ui';
import { usePhotos, type Photos } from './usePhotos';

/**
 * « Les photos de la soirée » — the shared library: every guest adds photos,
 * everyone holding the party link sees them, and they stay until the organiser
 * deletes the party. `photoCount` (from the live state) refreshes the list.
 */
export function PhotosCard({ joinToken, photoCount, className }: { joinToken: string; photoCount?: number; className?: string }) {
  const photos = usePhotos(joinToken, photoCount);
  // null: closed; '': the grid; an id: that photo, full screen.
  const [open, setOpen] = useState<string | null>(null);
  const { list } = photos;
  if (!list) return null;
  const count = list.photos.length;
  // After the party, someone who never joined has nothing to see here.
  if (count === 0 && !list.canUpload) return null;

  return (
    <section className={cn('rounded-3xl bg-surface p-4', className)}>
      <button onClick={() => setOpen('')} disabled={count === 0} className="flex w-full items-center gap-3 text-left">
        <div className="min-w-0 flex-1">
          <div className="font-display font-bold">📷 Les photos de la soirée</div>
          <p className="text-xs text-ink-dim">
            {count > 0
              ? `${count} photo${count > 1 ? 's' : ''} partagée${count > 1 ? 's' : ''}, à regarder et à garder`
              : 'Partage tes photos : tous les invités pourront les voir et les garder.'}
          </p>
        </div>
        {count > 0 && <span className="shrink-0 text-sm font-semibold text-pink">Tout voir ›</span>}
      </button>
      {count > 0 && (
        <div className="mt-3 grid grid-cols-4 gap-1.5">
          {list.photos.slice(0, 4).map((p, i) => (
            <button key={p.id} onClick={() => setOpen(p.id)} className="relative aspect-square overflow-hidden rounded-xl bg-surface-2">
              <img src={p.thumbUrl} alt={photoAlt(p)} className="h-full w-full object-cover" />
              {i === 3 && count > 4 && <span className="absolute inset-0 flex items-center justify-center bg-night/60 font-bold">+{count - 3}</span>}
            </button>
          ))}
        </div>
      )}
      {list.canUpload && <AddPhotos photos={photos} label="📸 Ajouter des photos" className="mt-3 w-full bg-surface-2" />}
      <UploadStatus photos={photos} />
      {open !== null && <PhotoGallery photos={photos} start={open || null} onClose={() => setOpen(null)} />}
    </section>
  );
}

const photoAlt = (p: PublicPhoto) => (p.guest ? `Photo de ${p.guest.pseudo}` : 'Photo de la soirée');

/** No `capture`: the phone offers both the camera and the photo library, several photos at once. */
function AddPhotos({ photos, label, className }: { photos: Photos; label: string; className?: string }) {
  const input = useRef<HTMLInputElement>(null);
  return (
    <>
      <button onClick={() => input.current?.click()} className={cn('shrink-0 rounded-2xl px-4 py-2.5 font-bold transition active:scale-[0.98]', className)}>
        {label}
      </button>
      <input
        ref={input}
        type="file"
        accept="image/*"
        multiple
        className="hidden"
        onChange={(e) => {
          const files = [...(e.target.files ?? [])];
          e.target.value = '';
          photos.add(files);
        }}
      />
    </>
  );
}

function UploadStatus({ photos }: { photos: Photos }) {
  const { upload, error } = photos;
  if (upload) {
    return (
      <div className="mt-3">
        <div className="flex items-center gap-2 text-sm text-ink-dim">
          <Spinner className="h-4 w-4" />
          Envoi {upload.total > 1 ? `de la photo ${upload.rank} sur ${upload.total}` : 'de ta photo'}…
        </div>
        <div className="mt-1.5 h-1.5 overflow-hidden rounded-full bg-surface-2">
          <div className="bg-gradient-neon h-full transition-[width]" style={{ width: `${Math.round(upload.progress * 100)}%` }} />
        </div>
      </div>
    );
  }
  return error ? <p className="mt-2 text-sm text-danger">{error}</p> : null;
}

function PhotoGallery({ photos, start, onClose }: { photos: Photos; start: string | null; onClose(): void }) {
  const [viewing, setViewing] = useState<string | null>(start);
  const list = photos.list!;
  const total = list.photos.reduce((n, p) => n + p.size, 0);

  useEffect(() => {
    if (viewing) return;
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [viewing, onClose]);

  return (
    <div className="fixed inset-0 z-40 flex flex-col bg-night/95 backdrop-blur-sm" role="dialog" aria-modal aria-label="Les photos de la soirée">
      <div className="mx-auto flex w-full max-w-md flex-1 flex-col overflow-hidden px-4 pt-4">
        <div className="flex items-center gap-2">
          <h2 className="font-display min-w-0 flex-1 truncate text-lg font-bold">📷 Photos</h2>
          {list.canUpload && <AddPhotos photos={photos} label="📸 Ajouter" className="bg-gradient-neon" />}
          <button onClick={onClose} className="rounded-2xl bg-surface-2 px-4 py-2.5 font-semibold">
            Fermer
          </button>
        </div>
        <UploadStatus photos={photos} />
        <div className="mt-3 flex-1 overflow-y-auto pb-8">
          <div className="grid grid-cols-3 gap-1">
            {list.photos.map((p) => (
              <button key={p.id} onClick={() => setViewing(p.id)} className="relative aspect-square overflow-hidden rounded-lg bg-surface-2">
                <img src={p.thumbUrl} alt={photoAlt(p)} loading="lazy" className="h-full w-full object-cover" />
                {p.guest && <Avatar guest={p.guest} size="sm" className="absolute bottom-1 left-1" />}
              </button>
            ))}
          </div>
          {list.photos.length > 0 && (
            <div className="mt-6 text-center">
              <a href={list.zipUrl} download className="inline-block rounded-2xl bg-surface-2 px-5 py-3 font-semibold">
                ⬇︎ Tout télécharger <span className="text-ink-faint">(.zip, {formatBytes(total)})</span>
              </a>
              <p className="mt-3 text-[11px] leading-snug text-ink-faint">
                Les photos restent disponibles jusqu’à ce que l’organisateur supprime la soirée. Leurs données de localisation sont retirées à l’envoi.
              </p>
            </div>
          )}
        </div>
      </div>
      {viewing && <PhotoViewer photos={photos} id={viewing} onNavigate={setViewing} onClose={() => setViewing(null)} />}
    </div>
  );
}

/** On a phone, « Enregistrer » goes through the share sheet: that is what lands a photo in the camera roll. */
const shareFiles = (() => {
  try {
    return matchMedia('(pointer: coarse)').matches && !!navigator.canShare?.({ files: [new File([], 'p.jpg', { type: 'image/jpeg' })] });
  } catch {
    return false;
  }
})();

function PhotoViewer({ photos, id, onNavigate, onClose }: { photos: Photos; id: string; onNavigate(id: string): void; onClose(): void }) {
  const items = photos.list!.photos;
  const i = items.findIndex((p) => p.id === id);
  const photo = items[i];
  // Newest first: the previous one is the newer one.
  const newer = items[i - 1];
  const older = items[i + 1];
  const [file, setFile] = useState<File | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const swipe = useRef<{ x: number; y: number } | null>(null);

  useEffect(() => {
    if (!photo) onClose(); // deleted meanwhile
  }, [photo, onClose]);

  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') onClose();
      else if (e.key === 'ArrowLeft' && newer) onNavigate(newer.id);
      else if (e.key === 'ArrowRight' && older) onNavigate(older.id);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [newer, older, onNavigate, onClose]);

  // The share sheet must open within the tap: have the file ready beforehand
  // (straight from the HTTP cache, the photo is already on screen).
  useEffect(() => {
    setFile(null);
    setError(null);
    if (!photo || !shareFiles) return;
    let alive = true;
    void fetch(photo.url)
      .then((r) => r.blob())
      .then((b) => alive && setFile(new File([b], `karozik-${photo.id.slice(0, 8)}.jpg`, { type: 'image/jpeg' })))
      .catch(() => undefined);
    return () => {
      alive = false;
    };
  }, [photo]);

  // Warm the neighbours up so swiping feels instant.
  useEffect(() => {
    for (const p of [newer, older]) if (p) new Image().src = p.url;
  }, [newer, older]);

  if (!photo) return null;
  const mine = !!photo.guest && photo.guest.id === photos.list!.me;

  const remove = async () => {
    if (!confirm('Supprimer cette photo pour tout le monde ?')) return;
    setBusy(true);
    try {
      await photos.remove(photo.id);
      const next = older ?? newer;
      if (next) onNavigate(next.id);
      else onClose();
    } catch (e) {
      setError((e as Error).message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="fixed inset-0 z-50 flex flex-col bg-black" role="dialog" aria-modal aria-label={photoAlt(photo)}>
      <div className="flex items-center justify-between px-4 pt-4 pb-2">
        <span className="text-sm text-ink-dim">
          {i + 1} / {items.length}
        </span>
        <button onClick={onClose} className="rounded-full bg-white/10 px-4 py-2 font-semibold" aria-label="Fermer">
          ✕
        </button>
      </div>
      <div
        className="relative flex min-h-0 flex-1 items-center justify-center"
        onPointerDown={(e) => (swipe.current = { x: e.clientX, y: e.clientY })}
        onPointerUp={(e) => {
          const s = swipe.current;
          swipe.current = null;
          if (!s) return;
          const dx = e.clientX - s.x;
          if (Math.abs(dx) < 50 || Math.abs(dx) < Math.abs(e.clientY - s.y)) return;
          const target = dx > 0 ? newer : older;
          if (target) onNavigate(target.id);
        }}
      >
        <img key={photo.id} src={photo.url} alt={photoAlt(photo)} draggable={false} className="max-h-full max-w-full object-contain select-none" />
        {newer && (
          <button onClick={() => onNavigate(newer.id)} className="absolute top-1/2 left-2 hidden -translate-y-1/2 rounded-full bg-white/10 px-3 py-2 text-xl sm:block" aria-label="Photo précédente">
            ‹
          </button>
        )}
        {older && (
          <button onClick={() => onNavigate(older.id)} className="absolute top-1/2 right-2 hidden -translate-y-1/2 rounded-full bg-white/10 px-3 py-2 text-xl sm:block" aria-label="Photo suivante">
            ›
          </button>
        )}
      </div>
      <div className="safe-bottom px-4 pt-3">
        <div className="mx-auto flex max-w-md items-center gap-3">
          <Avatar guest={photo.guest} size="sm" />
          <div className="min-w-0 flex-1 text-sm">
            <div className="truncate font-semibold">{photo.guest?.pseudo ?? 'Un invité'}</div>
            <div className="text-xs text-ink-faint">{formatClock(photo.createdAt)}</div>
          </div>
          {mine && (
            <button onClick={() => void remove()} disabled={busy} className="rounded-xl bg-white/10 px-3 py-2 text-sm text-ink-dim disabled:opacity-40">
              Supprimer
            </button>
          )}
          {shareFiles && file ? (
            <button
              onClick={() => void navigator.share({ files: [file] }).catch(() => undefined)}
              className="bg-gradient-neon rounded-xl px-4 py-2 text-sm font-bold"
            >
              Enregistrer
            </button>
          ) : (
            <a href={`${photo.url}?download`} download className="bg-gradient-neon rounded-xl px-4 py-2 text-sm font-bold">
              Enregistrer
            </a>
          )}
        </div>
        {error && <p className="mx-auto mt-2 max-w-md text-sm text-danger">{error}</p>}
      </div>
    </div>
  );
}
